﻿import {ErrorBundle, errorToErrorBundle, FormattableString, isNullOrEmpty} from "@2d-front-monorepo/core";
import {JSX, useEffect, useRef} from "react";
import {getParentScrollable} from "../helpers/BoxModel";
import {scrollIntoView} from "../helpers/scrollIntoView";
import Message from "../HtmlControls/Message";
import {ErrorEntry} from "./MvvmContextState";
import {useMvvmContext} from "./MvvmDataProvider";

export type MvvmErrorCollectorProps = {
	title: FormattableString;
	detail?: FormattableString;
	footer?: FormattableString;
};

function formErrorsToErrorBundle(errorEntry: ErrorEntry, props: MvvmErrorCollectorProps): ErrorBundle {
	return {
		title: props.title,
		detail: props.detail,
		errors: [
			{
				text: errorEntry.error,
				args: {"value": errorEntry.value}
			}
		],
		footer: props.footer
	}
}

function customErrorToErrorBundle(exception: Error, props: MvvmErrorCollectorProps): ErrorBundle {
	const bundle = errorToErrorBundle(exception);
	if (isNullOrEmpty(bundle.detail?.text)){
		bundle.detail = bundle.title;
	}

	bundle.title = props.title;
	return bundle;
}

export default function MvvmErrorCollector(props: MvvmErrorCollectorProps): JSX.Element | undefined {
	const messageRef = useRef<HTMLDivElement>(null);
	const context = useMvvmContext<any>();

	// @ts-ignore
	const formError = context.getError("::form");
	const exception = context.hasExceptions() ? context.getExceptions()[0] : undefined;

	let errorBundle: ErrorBundle | undefined = undefined;
	if (formError) {
		errorBundle = formErrorsToErrorBundle(formError, props);
	} else if (exception) {
		errorBundle = customErrorToErrorBundle(exception, props);
	}

	useEffect(() => {
		const raf = requestAnimationFrame(() => {
			if (!errorBundle || !messageRef.current) {
				return;
			}

			const scrollable = getParentScrollable(messageRef.current);
			scrollIntoView(scrollable, messageRef.current, "top", "smooth");
		});

		return () => cancelAnimationFrame(raf);
	}, [errorBundle, messageRef]);

	if (!errorBundle) {
		return undefined;
	}

	return <Message title={errorBundle.title}
	                detail={errorBundle.detail}
	                items={errorBundle.errors}
	                footer={errorBundle.footer}
	                type="error"
	                ref={messageRef}/>;
}
